import React from "react";

import { Form, Checkbox } from "antd";
import IneligiblePatientWarning from "../IneligiblePatientWarning";
import useConditionalValidation from "./utils/useConditionalValidation";
import { useSanitizedHtml } from "../../../../../hooks/useSanitizedHtml";
import { HTML_SANITIZE_OPTIONS } from "../../../../../utils/sanitize";

export const CheckboxItem = ({
  name,
  // See sanitization note below.
  label,
  required = false,
  rules = [],
  errorMessage = "",
  shouldValidateFields = false,
  defaultValue = false,
  // Whether to seed a field-level initial value. antd lets the Form's `initialValues` win over a
  // Form.Item `initialValue`, so this `false` seed only applies as a FALLBACK when the form doesn't
  // provide one — which is what the hand-written forms and every mapped checkbox rely on to submit
  // `false` when untouched. The JSON engine turns this OFF only for an unmapped, no-default UI toggle so
  // it stays out of the submitted bundle when untouched (MVP-31021, Jehad QA Findings C / G).
  seedInitialValue = true,
  formItemProps = {},
  fieldProps = {},
  appendRequiredAsterisk = false,
  ...props
}) => {
  useConditionalValidation(name, shouldValidateFields);
  const rquiredRules = [];

  const sanitizedLabel = useSanitizedHtml(
    typeof label === "string" ? (required && appendRequiredAsterisk ? `${label} *` : label) : null,
    HTML_SANITIZE_OPTIONS
  );

  if (required)
    rquiredRules.push({
      validator: (_, value) =>
        value
          ? Promise.resolve()
          : Promise.reject(
              errorMessage ? (
                <IneligiblePatientWarning ineligibleAnswerError={new Error(errorMessage)} />
              ) : (
                new Error("")
              )
            )
    });

  return (
    <Form.Item
      rules={[{ required }, ...rquiredRules, ...rules]}
      name={name}
      valuePropName="checked"
      {...(seedInitialValue ? { initialValue: defaultValue } : {})}
      {...props}
      {...formItemProps}
    >
      <Checkbox {...fieldProps} required={required}>
        {/*
          String `label` is sanitized here via `sanitizeHtml` before being dangerously set as HTML.
          The non-string branch lets a caller pass an already-safe React node instead (e.g.
          CheckboxWithContentField's rich-text attestation span for BMS Copay LS's Privacy Statement
          checkbox), rendered as-is and bypassing sanitizeHtml — those callers own the node's safety.
        */}
        {typeof label === "string" ? (
          <span
            dangerouslySetInnerHTML={{
              __html: sanitizedLabel
            }}
          />
        ) : (
          <span>
            {label}
            {required && appendRequiredAsterisk ? " *" : null}
          </span>
        )}
      </Checkbox>
    </Form.Item>
  );
};
